'use client'
import { useEffect, useMemo, useRef, useState } from "react"
import debounce from "lodash/debounce"

import { AgenceArticle, ListAgenceArticlePayload } from "@/domains/agenceArticle/agenceArticle.types"
import { NewsCard } from "@/stories/components/NewsCard/NewsCard"
import { Grid } from "../Grid/Grid"
import { Filters } from "../Search/Filters/Filters"
import { Section, SectionAction } from "../Section/Section"
import { listAgenceArticle } from "@/domains/agenceArticle/agenceArticle.api"
import { Loader } from "@/stories/atoms/Loader/Loader"
import { Button } from "@/stories/atoms/Button/Button"
import { Row } from "../Grid/Row"
import { Icon } from "@/stories/atoms/Icon/Icon"
import useIsMobile from "@/hooks/useMobile"
import { useModal } from "../Modal/Modal"
import { FiltersDialog } from "../Search/SearchPage/FiltersDialog"
import { ArticleFilters, ArticleFiltersState } from "./ArticleFilters"
import { GeoEntity } from "@/utils/geo-search"

export const ArticleList = ({id}: {id: string}) => {
  const [list, setList] = useState<AgenceArticle[]>([])
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState<{nbPages: number, total: number, currentPage: number}>()
  // Modale filters
  const ref = useRef<HTMLDialogElement | null>(null)
  const {isMobile} = useIsMobile()
  const {setOpen} = useModal({ref})
  const [page, setPage] = useState(1)
  const [locality, setLocality] = useState<GeoEntity | null>(null)

  const [filters, setFilters] = useState<ArticleFiltersState>({
    search: '',
    service: [],
    type: '',
    codeInsee: '',
    codeDepartement: '',
  })

  const fetchArticles = useMemo(() =>
    debounce(async (agenceId: string, params: ListAgenceArticlePayload) => {
      setLoading(true)
      const data = await listAgenceArticle(agenceId, {
        statut: 'published',
        page: params.page,
        perPage: '12',
        search: params.search as string,
        service: params.service,
        type: params.type as string,
        ...(params.codeInsee ? { codeInsee: params.codeInsee } : {}),
        ...(params.codeDepartement ? { codeDepartement: params.codeDepartement } : {}),
      })
      setMeta({
        nbPages: data.meta.last_page,
        total: data.meta.total,
        currentPage: data.meta.current_page
      })
      setList(data.items)
      setLoading(false)
    }, 300)
  , [])

  useEffect(() => {
    fetchArticles(id, {
      page,
      ...filters,
      service: filters.service.map(service => service.value)
    })
    return () => fetchArticles.cancel()
  }, [fetchArticles, id, filters, page])

  useEffect(() => {
    document.body.classList.add('search-page')
  }, [])

  // service est un tableau : un simple test « !== '' » le considérerait toujours actif
  const hasActiveFilters = Object.values(filters).some(value => Array.isArray(value) ? value.length > 0 : value !== '')

  // tout changement de filtre repart de la page 1 : la page courante peut ne plus exister dans les nouveaux résultats
  const updateFilters = (newFilters: ArticleFiltersState) => {
    setPage(1)
    setFilters(newFilters)
  }

  const clear = () => {
    setLocality(null)
    updateFilters({
      ...filters,
      search: '',
      service: [],
      type: '',
      codeInsee: '',
      codeDepartement: '',
    })
  }

  return (
    <>
      <Filters>
        {isMobile ? (
          <>
            <Button onClick={() => setOpen(true)}>
              <span className="hide-sub-md">Filtres</span>
              <Icon icon="filter"/>
            </Button>
            <FiltersDialog
              ref={ref}
              onClose={() => setOpen(false)}
              actions={
                <>
                  <Button variant='secondary' type="button" onClick={clear} disabled={!hasActiveFilters}>
                    <Icon icon='arrow-rotate-left'/>
                    <span className="sr-only">Réinitialiser</span>
                  </Button>
                  <Button onClick={() => setOpen(false)} onAuxClick={() => setOpen(false)}>Afficher les contenus</Button>
                </>
              }
              title='Filtres'>
              <ArticleFilters filters={filters} locality={locality} setFilters={updateFilters} setLocality={setLocality} />
            </FiltersDialog>
          </>
        ) : (
          <Row>
            <ArticleFilters filters={filters} locality={locality} setFilters={updateFilters} setLocality={setLocality} />
            <Button variant='secondary' type="button" onClick={clear} disabled={!hasActiveFilters}>
              <Icon icon='arrow-rotate-left'/>
              <span className="sr-only">Réinitialiser</span>
            </Button>
          </Row>
        )}
      </Filters>
      <Section paddingStart="m">
        { loading ? <Loader/> : list.length > 0 && (
          <>
            {meta?.total && <Row> { meta?.total} contenu{ meta?.total > 1 ? 's' : '' } trouvé{ meta?.total > 1 ? 's' : '' }</Row>}
            <Grid nbCols={4}>
              {list.map(article => (
                <NewsCard
                  key={article.id}
                  title={article.titre}
                  photo={{
                    src: article.imageCouvertureUrl,
                    alt: article.imageCouvertureAlt
                  }}
                  type={article.type}
                  slug={article.slug}
                  date={article.createdAt || undefined}
                  services={article.services}
                  />
              ))}
            </Grid>
            <SectionAction>
              {[...Array(meta?.nbPages).keys()].map((page, i) => (
                <Button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  variant={i + 1 === meta?.currentPage ? 'secondary' : 'primary'}
                  disabled={i + 1 === meta?.currentPage}
                  type="button">
                  {i + 1}
                </Button>
              ))}
            </SectionAction>
          </>
        )}
      </Section>
    </>
  )
}